import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useReservations } from '../ReservationContext';

const FloorMapView = () => {
  const {
    zones,
    tables,
    floorMapMode,
    setFloorMapMode,
    selectedFloor,
    setSelectedFloor,
    floorLayout,
    setFloorLayout,
    loadFloorLayout,
    saveFloorLayout,
    getZoneColor,
    loading,
  } = useReservations();

  const canvasRef = useRef(null);

  // Multi-select state - array of { type: 'zone'|'table', id: string }
  const [selectedItems, setSelectedItems] = useState([]);

  // Drag state - track what's being dragged and the grab offset
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragStartPositions, setDragStartPositions] = useState({});

  // Resize state - now supports both zones and tables
  const [resizing, setResizing] = useState(null); // { type: 'zone'|'table', id: string, handle: string }
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, itemX: 0, itemY: 0 });

  // Min sizes for resizing
  const MIN_ZONE_WIDTH = 150;
  const MIN_ZONE_HEIGHT = 100;
  const MIN_TABLE_SIZE = 30;

  useEffect(() => {
    if (zones.length > 0 || tables.length > 0) {
      loadFloorLayout();
    }
  }, [zones, tables, loadFloorLayout]);

  // Helper to check if an item is selected
  const isItemSelected = useCallback((type, id) => {
    return selectedItems.some(item => item.type === type && item.id === id);
  }, [selectedItems]);

  // Handle mouse move for resizing
  const handleMouseMove = useCallback((e) => {
    if (!resizing) return;

    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;

    const minWidth = resizing.type === 'zone' ? MIN_ZONE_WIDTH : MIN_TABLE_SIZE;
    const minHeight = resizing.type === 'zone' ? MIN_ZONE_HEIGHT : MIN_TABLE_SIZE;

    let newWidth = resizeStart.width;
    let newHeight = resizeStart.height;
    let newX = resizeStart.itemX;
    let newY = resizeStart.itemY;

    // Handle different resize handles
    if (resizing.handle.includes('e')) {
      newWidth = Math.max(minWidth, resizeStart.width + deltaX);
    }
    if (resizing.handle.includes('w')) {
      const widthChange = Math.min(deltaX, resizeStart.width - minWidth);
      newWidth = resizeStart.width - widthChange;
      newX = resizeStart.itemX + widthChange;
    }
    if (resizing.handle.includes('s')) {
      newHeight = Math.max(minHeight, resizeStart.height + deltaY);
    }
    if (resizing.handle.includes('n')) {
      const heightChange = Math.min(deltaY, resizeStart.height - minHeight);
      newHeight = resizeStart.height - heightChange;
      newY = resizeStart.itemY + heightChange;
    }

    if (resizing.type === 'zone') {
      setFloorLayout(prev => ({
        ...prev,
        zones: {
          ...prev.zones,
          [resizing.id]: {
            ...prev.zones[resizing.id],
            width: newWidth,
            height: newHeight,
            x: newX,
            y: newY,
          },
        },
      }));
    } else if (resizing.type === 'table') {
      setFloorLayout(prev => ({
        ...prev,
        tables: {
          ...prev.tables,
          [resizing.id]: {
            ...prev.tables[resizing.id],
            width: newWidth,
            height: newHeight,
            x: newX,
            y: newY,
          },
        },
      }));
    }
  }, [resizing, resizeStart, setFloorLayout]);

  // Handle mouse up to stop resizing
  const handleMouseUp = useCallback(() => {
    if (resizing) {
      setResizing(null);
    }
  }, [resizing]);

  // Handle drag move - moves all selected items together
  const handleDragMove = useCallback((e) => {
    if (!dragging) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate delta from the drag start position of the primary dragged item
    const primaryStartPos = dragStartPositions[`${dragging.type}-${dragging.id}`];
    if (!primaryStartPos) return;

    const deltaX = mouseX - dragOffset.x - primaryStartPos.x;
    const deltaY = mouseY - dragOffset.y - primaryStartPos.y;

    setFloorLayout(prev => {
      const newLayout = { ...prev, zones: { ...prev.zones }, tables: { ...prev.tables } };

      // Move all selected items
      selectedItems.forEach(item => {
        const startPos = dragStartPositions[`${item.type}-${item.id}`];
        if (!startPos) return;

        if (item.type === 'zone' && newLayout.zones[item.id]) {
          newLayout.zones[item.id] = {
            ...newLayout.zones[item.id],
            x: Math.max(0, startPos.x + deltaX),
            y: Math.max(0, startPos.y + deltaY),
          };
        } else if (item.type === 'table' && newLayout.tables[item.id]) {
          newLayout.tables[item.id] = {
            ...newLayout.tables[item.id],
            x: Math.max(0, startPos.x + deltaX),
            y: Math.max(0, startPos.y + deltaY),
          };
        }
      });

      return newLayout;
    });
  }, [dragging, dragOffset, dragStartPositions, selectedItems, setFloorLayout]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setDragging(null);
    setDragStartPositions({});
  }, []);

  // Add/remove global mouse listeners for resize and drag
  useEffect(() => {
    if (resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [resizing, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [dragging, handleDragMove, handleDragEnd]);

  const floors = [...new Set(zones.map(z => z.floor || 1))].sort();
  const currentFloorZones = Object.values(floorLayout.zones).filter(z => z.floor === selectedFloor);
  const currentFloorTables = Object.values(floorLayout.tables).filter(t => {
    const zone = floorLayout.zones[t.zone_id];
    return !zone || zone.floor === selectedFloor;
  });

  // Start dragging - capture the offset where user clicked within the element
  const handleDragStart = (e, itemType, itemId) => {
    if (floorMapMode !== 'edit' || resizing) return;
    e.preventDefault();
    e.stopPropagation();

    const element = e.currentTarget;
    const elementRect = element.getBoundingClientRect();

    // Calculate where within the element the user clicked
    const offsetX = e.clientX - elementRect.left;
    const offsetY = e.clientY - elementRect.top;

    // Check if this item is already selected
    const alreadySelected = isItemSelected(itemType, itemId);

    // Determine the new selection
    let newSelection;
    if (e.shiftKey) {
      // Shift+click: toggle item in selection
      if (alreadySelected) {
        newSelection = selectedItems.filter(item => !(item.type === itemType && item.id === itemId));
      } else {
        newSelection = [...selectedItems, { type: itemType, id: itemId }];
      }
    } else {
      // Normal click: if item is already selected, keep multi-selection; otherwise select only this
      if (alreadySelected && selectedItems.length > 1) {
        newSelection = selectedItems;
      } else {
        newSelection = [{ type: itemType, id: itemId }];
      }
    }

    setSelectedItems(newSelection);

    // Store start positions for all selected items (including the new selection)
    const startPositions = {};
    newSelection.forEach(item => {
      if (item.type === 'zone' && floorLayout.zones[item.id]) {
        startPositions[`zone-${item.id}`] = {
          x: floorLayout.zones[item.id].x,
          y: floorLayout.zones[item.id].y,
        };
      } else if (item.type === 'table' && floorLayout.tables[item.id]) {
        startPositions[`table-${item.id}`] = {
          x: floorLayout.tables[item.id].x,
          y: floorLayout.tables[item.id].y,
        };
      }
    });

    setDragStartPositions(startPositions);
    setDragOffset({ x: offsetX, y: offsetY });
    setDragging({ type: itemType, id: itemId });
  };

  const handleCanvasClick = (e) => {
    // Only clear selection if clicking directly on canvas (not on an item)
    if (e.target === canvasRef.current || e.target.classList.contains('floormap-grid')) {
      setSelectedItems([]);
    }
  };

  // Generic resize start handler for both zones and tables
  const handleResizeStart = (e, itemType, itemId, handle) => {
    e.stopPropagation();
    e.preventDefault();

    const item = itemType === 'zone' ? floorLayout.zones[itemId] : floorLayout.tables[itemId];
    if (!item) return;

    setResizing({ type: itemType, id: itemId, handle });
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: item.width,
      height: item.height,
      itemX: item.x,
      itemY: item.y,
    });
    setSelectedItems([{ type: itemType, id: itemId }]);
  };

  // Property update functions - now work with multi-select
  const updateZoneColor = (color) => {
    const selectedZones = selectedItems.filter(item => item.type === 'zone');
    if (selectedZones.length === 0) return;

    setFloorLayout(prev => {
      const newZones = { ...prev.zones };
      selectedZones.forEach(item => {
        if (newZones[item.id]) {
          newZones[item.id] = { ...newZones[item.id], color };
        }
      });
      return { ...prev, zones: newZones };
    });
  };

  const updateTableShape = (shape) => {
    const selectedTables = selectedItems.filter(item => item.type === 'table');
    if (selectedTables.length === 0) return;

    setFloorLayout(prev => {
      const newTables = { ...prev.tables };
      selectedTables.forEach(item => {
        if (newTables[item.id]) {
          newTables[item.id] = { ...newTables[item.id], shape };
        }
      });
      return { ...prev, tables: newTables };
    });
  };

  const rotateTable = () => {
    const selectedTables = selectedItems.filter(item => item.type === 'table');
    if (selectedTables.length === 0) return;

    setFloorLayout(prev => {
      const newTables = { ...prev.tables };
      selectedTables.forEach(item => {
        if (newTables[item.id]) {
          newTables[item.id] = {
            ...newTables[item.id],
            rotation: ((newTables[item.id].rotation || 0) + 45) % 360,
          };
        }
      });
      return { ...prev, tables: newTables };
    });
  };

  // Get counts for toolbar display
  const selectedZonesCount = selectedItems.filter(item => item.type === 'zone').length;
  const selectedTablesCount = selectedItems.filter(item => item.type === 'table').length;
  const totalSelected = selectedItems.length;

  // For single selection, get the specific item for detailed editing
  const singleSelectedZone = selectedZonesCount === 1 && selectedTablesCount === 0
    ? floorLayout.zones[selectedItems.find(item => item.type === 'zone')?.id]
    : null;
  const singleSelectedTable = selectedTablesCount === 1 && selectedZonesCount === 0
    ? floorLayout.tables[selectedItems.find(item => item.type === 'table')?.id]
    : null;

  const renderTable = (table) => {
    const isSelected = isItemSelected('table', table.table_id);
    const isResizingThis = resizing?.type === 'table' && resizing?.id === table.table_id;
    const isDragging = dragging?.type === 'table' && dragging?.id === table.table_id;
    const shape = table.shape || 'square';
    const rotation = table.rotation || 0;
    const width = table.width || 50;
    const height = table.height || 50;

    let shapeClass = 'map-table-square';
    if (shape === 'round') shapeClass = 'map-table-round';
    if (shape === 'rectangle') shapeClass = 'map-table-rectangle';
    if (shape === 'bar') shapeClass = 'map-table-bar';

    return (
      <div
        key={table.table_id}
        className={`map-table ${shapeClass} ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isResizingThis ? 'resizing' : ''} table-status-${table.status || 'available'}`}
        style={{
          left: table.x,
          top: table.y,
          width: width,
          height: height,
          transform: `rotate(${rotation}deg)`,
          cursor: floorMapMode === 'edit' ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
        }}
        onMouseDown={(e) => {
          if (e.target.classList.contains('resize-handle')) return;
          handleDragStart(e, 'table', table.table_id);
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="map-table-id">{table.table_id}</span>
        <span className="map-table-capacity">{table.capacity}</span>

        {/* Resize handles - only show in edit mode when table is selected */}
        {floorMapMode === 'edit' && isSelected && (
          <>
            {/* Corner handles */}
            <div
              className="resize-handle resize-handle-table resize-nw"
              onMouseDown={(e) => handleResizeStart(e, 'table', table.table_id, 'nw')}
            />
            <div
              className="resize-handle resize-handle-table resize-ne"
              onMouseDown={(e) => handleResizeStart(e, 'table', table.table_id, 'ne')}
            />
            <div
              className="resize-handle resize-handle-table resize-sw"
              onMouseDown={(e) => handleResizeStart(e, 'table', table.table_id, 'sw')}
            />
            <div
              className="resize-handle resize-handle-table resize-se"
              onMouseDown={(e) => handleResizeStart(e, 'table', table.table_id, 'se')}
            />
          </>
        )}
      </div>
    );
  };

  const renderZone = (zone) => {
    const isSelected = isItemSelected('zone', zone.zone_id);
    const isResizingThis = resizing?.type === 'zone' && resizing?.id === zone.zone_id;
    const isDragging = dragging?.type === 'zone' && dragging?.id === zone.zone_id;

    return (
      <div
        key={zone.zone_id}
        className={`map-zone ${isSelected ? 'selected' : ''} ${isResizingThis ? 'resizing' : ''} ${isDragging ? 'dragging' : ''}`}
        style={{
          left: zone.x,
          top: zone.y,
          width: zone.width,
          height: zone.height,
          backgroundColor: zone.color || getZoneColor(zone.zone_id),
          cursor: floorMapMode === 'edit' && !resizing ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
        onMouseDown={(e) => {
          // Don't start drag if clicking on a resize handle
          if (e.target.classList.contains('resize-handle')) return;
          handleDragStart(e, 'zone', zone.zone_id);
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="map-zone-label">{zone.zone_name}</div>

        {/* Resize handles - only show in edit mode when zone is selected */}
        {floorMapMode === 'edit' && isSelected && (
          <>
            {/* Corner handles */}
            <div
              className="resize-handle resize-nw"
              onMouseDown={(e) => handleResizeStart(e, 'zone', zone.zone_id, 'nw')}
            />
            <div
              className="resize-handle resize-ne"
              onMouseDown={(e) => handleResizeStart(e, 'zone', zone.zone_id, 'ne')}
            />
            <div
              className="resize-handle resize-sw"
              onMouseDown={(e) => handleResizeStart(e, 'zone', zone.zone_id, 'sw')}
            />
            <div
              className="resize-handle resize-se"
              onMouseDown={(e) => handleResizeStart(e, 'zone', zone.zone_id, 'se')}
            />
            {/* Edge handles */}
            <div
              className="resize-handle resize-n"
              onMouseDown={(e) => handleResizeStart(e, 'zone', zone.zone_id, 'n')}
            />
            <div
              className="resize-handle resize-s"
              onMouseDown={(e) => handleResizeStart(e, 'zone', zone.zone_id, 's')}
            />
            <div
              className="resize-handle resize-e"
              onMouseDown={(e) => handleResizeStart(e, 'zone', zone.zone_id, 'e')}
            />
            <div
              className="resize-handle resize-w"
              onMouseDown={(e) => handleResizeStart(e, 'zone', zone.zone_id, 'w')}
            />
          </>
        )}
      </div>
    );
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  return (
    <div className="floormap-view">
      <div className="floormap-toolbar">
        <div className="toolbar-left">
          <h3>Floor Map Designer</h3>
          <div className="floor-selector">
            {floors.map(floor => (
              <button
                key={floor}
                className={`floor-btn ${selectedFloor === floor ? 'active' : ''}`}
                onClick={() => setSelectedFloor(floor)}
              >
                Floor {floor}
              </button>
            ))}
          </div>
        </div>
        <div className="toolbar-right">
          {floorMapMode === 'view' ? (
            <button className="btn btn-primary" onClick={() => setFloorMapMode('edit')}>
              Edit Layout
            </button>
          ) : (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setFloorMapMode('view');
                  setSelectedItems([]);
                  setResizing(null);
                  loadFloorLayout();
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={saveFloorLayout}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Layout'}
              </button>
            </>
          )}
        </div>
      </div>

      {floorMapMode === 'edit' && (
        <div className="floormap-help">
          Drag to move items. Shift+click to select multiple. Drag corners to resize.
        </div>
      )}

      <div className="floormap-container">
        {/* Floor Map Canvas */}
        <div
          ref={canvasRef}
          className={`floormap-canvas ${resizing ? 'is-resizing' : ''} ${dragging ? 'is-dragging' : ''}`}
          onClick={handleCanvasClick}
        >
          {/* Loading State */}
          {(zones.length === 0 && tables.length === 0) ? (
            <div className="floormap-loading">
              <div className="floormap-spinner"></div>
              <span>Loading floor map...</span>
            </div>
          ) : (
            <>
              {/* Render Zones */}
              {currentFloorZones.map(zone => renderZone(zone))}

              {/* Render Tables */}
              {currentFloorTables.map(table => renderTable(table))}

              {/* Grid overlay in edit mode */}
              {floorMapMode === 'edit' && <div className="floormap-grid" />}
            </>
          )}
        </div>
      </div>

      {/* Selection Toolbar */}
      {floorMapMode === 'edit' && totalSelected > 0 && (
        <div className="floormap-selection-toolbar">
          {/* Multi-select label */}
          {totalSelected > 1 ? (
            <>
              <span className="toolbar-label">
                {totalSelected} items selected
                {selectedZonesCount > 0 && ` (${selectedZonesCount} zone${selectedZonesCount > 1 ? 's' : ''})`}
                {selectedTablesCount > 0 && ` (${selectedTablesCount} table${selectedTablesCount > 1 ? 's' : ''})`}
              </span>
              <div className="toolbar-divider" />
              {/* Show color picker if any zones selected */}
              {selectedZonesCount > 0 && (
                <>
                  <span className="toolbar-section-label">Color:</span>
                  {['#e0e7ff', '#fce7f3', '#dcfce7', '#fef3c7', '#dbeafe', '#f3f4f6', '#fee2e2', '#d1fae5'].map(color => (
                    <button
                      key={color}
                      className="color-swatch-small"
                      style={{ backgroundColor: color }}
                      onClick={() => updateZoneColor(color)}
                    />
                  ))}
                  <div className="toolbar-divider" />
                </>
              )}
              {/* Show shape options if any tables selected */}
              {selectedTablesCount > 0 && (
                <>
                  <span className="toolbar-section-label">Shape:</span>
                  {['round', 'square', 'rectangle', 'bar'].map(shape => (
                    <button
                      key={shape}
                      className="toolbar-shape-btn"
                      onClick={() => updateTableShape(shape)}
                    >
                      {shape}
                    </button>
                  ))}
                  <div className="toolbar-divider" />
                  <button className="toolbar-action-btn" onClick={rotateTable}>
                    Rotate 45°
                  </button>
                </>
              )}
            </>
          ) : singleSelectedZone ? (
            <>
              <span className="toolbar-label">{singleSelectedZone.zone_name}</span>
              <div className="toolbar-divider" />
              <span className="toolbar-section-label">Color:</span>
              {['#e0e7ff', '#fce7f3', '#dcfce7', '#fef3c7', '#dbeafe', '#f3f4f6', '#fee2e2', '#d1fae5'].map(color => (
                <button
                  key={color}
                  className={`color-swatch-small ${singleSelectedZone.color === color ? 'active' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => updateZoneColor(color)}
                />
              ))}
            </>
          ) : singleSelectedTable ? (
            <>
              <span className="toolbar-label">Table {singleSelectedTable.table_id}</span>
              <div className="toolbar-divider" />
              <span className="toolbar-section-label">Shape:</span>
              {['round', 'square', 'rectangle', 'bar'].map(shape => (
                <button
                  key={shape}
                  className={`toolbar-shape-btn ${singleSelectedTable.shape === shape ? 'active' : ''}`}
                  onClick={() => updateTableShape(shape)}
                >
                  {shape}
                </button>
              ))}
              <div className="toolbar-divider" />
              <button className="toolbar-action-btn" onClick={rotateTable}>
                Rotate 45°
              </button>
            </>
          ) : null}
          <div className="toolbar-spacer" />
          <button className="toolbar-close-btn" onClick={clearSelection}>
            Done
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="floormap-legend">
        <div className="legend-item">
          <span className="legend-dot available" />
          <span>Available</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot occupied" />
          <span>Occupied</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot reserved" />
          <span>Reserved</span>
        </div>
      </div>
    </div>
  );
};

export default FloorMapView;
